import React, { FC, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  Autorenew as AutorenewIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Refresh as RefreshIcon,
  Replay as ReplayIcon,
} from '@material-ui/icons';
import { useQuery } from '@apollo/client';
import {
  DATA_EXPORT_BATCHES,
  DataExportBatch,
  DataExportBatchesData,
  DataExportBatchesVars,
  DataExportDefinition,
  DataExportDeliveryChannel,
  DataExportRun,
  REDELIVER_BATCH,
  RETRY_DATA_EXPORT_BATCH,
  RedeliverBatchData,
  RedeliverBatchVars,
  RetryDataExportBatchData,
  RetryDataExportBatchVars,
  TRIGGER_DATA_EXPORT,
  TriggerDataExportData,
  TriggerDataExportVars,
} from '@/graph/data-export-admin';
import SelectInput from '@/se/components/inputs/SelectInput';
import DateFilter from '@/components/pages/analytics/DateFilter';
import { NamedRange } from '@/components/core/DatePicker';
import {
  BatchDownloadButton,
  BatchStatusChip,
  CopyableRunId,
  DryRunControl,
  HISTORY_PAGE_SIZE,
  InvokeButton,
  ResultDisplay,
  RunStatusChip,
  SectionShell,
  backfillOutcome,
  definitionKey,
  exportLabel,
  redeliverOutcome,
  useTimestampFormatter,
  formatWindow,
  useInvoker,
  useSharedStyles,
} from './shared';
import { BatchDeliveriesExpansion } from './batch-deliveries';

export const ManualTab: FC<{
  organizationId: number;
  definitions: DataExportDefinition[];
  channels: DataExportDeliveryChannel[];
  /** When false, the Dry-run checkbox on the trigger panel is hidden. */
  advancedMode: boolean;
}> = ({ organizationId, definitions, channels, advancedMode }) => {
  const classes = useSharedStyles();
  const [refreshKey, setRefreshKey] = useState(0);
  const bumpHistory = () => setRefreshKey(k => k + 1);

  return (
    <div className={classes.manualSplit}>
      <div className={classes.manualFormCol}>
        <ManualTriggerPanel
          organizationId={organizationId}
          definitions={definitions}
          channels={channels}
          advancedMode={advancedMode}
          onTriggerSettled={bumpHistory}
          vertical
        />
      </div>
      <div className={classes.manualTableCol}>
        <BatchHistoryTable
          organizationId={organizationId}
          triggeredBy={['manual']}
          channels={channels}
          advancedMode={advancedMode}
          refreshKey={refreshKey}
          title="Recent manual exports"
          emptyMessage="No manual exports have been triggered yet."
          onActionSettled={bumpHistory}
          hideTriggeredColumn
          hideCreatedColumn
          hideRefreshButton
        />
      </div>
    </div>
  );
};

/* ---------- trigger panel ---------- */

const ManualTriggerPanel: FC<{
  organizationId: number;
  definitions: DataExportDefinition[];
  channels: DataExportDeliveryChannel[];
  advancedMode: boolean;
  onTriggerSettled: () => void;
  /** Stack the controls vertically and stretch them to fill the column. */
  vertical?: boolean;
}> = ({ organizationId, definitions, channels, advancedMode, onTriggerSettled, vertical = false }) => {
  const classes = useSharedStyles();
  const fullWhenVertical = vertical ? classes.fieldFull : '';
  const [selectedKey, setSelectedKey] = useState('');
  // Date range as a js-joda `Range` (same value the Analytics/Patient
  // Feedback filters use). Defaults to "Last 7 days" so the operator can
  // trigger immediately; `firstIncluded()`/`lastIncluded()` map it to the
  // end-inclusive start/end strings the trigger mutation expects.
  const [range, setRange] = useState(() => NamedRange.lastSevenDays());
  const [channelIds, setChannelIds] = useState<number[]>([]);
  const [dryRun, setDryRun] = useState(false);
  // Reset dryRun when advanced mode is hidden so a stale `true` from
  // a previous toggle session does not silently turn the next real
  // trigger into a no-op dry run. The user can re-toggle Shift+D to
  // see the checkbox again.
  React.useEffect(() => {
    if (!advancedMode) setDryRun(false);
  }, [advancedMode]);

  const { loading, result, invoke } = useInvoker<TriggerDataExportData, TriggerDataExportVars>({
    mutation: TRIGGER_DATA_EXPORT,
    extractResult: d => backfillOutcome(d?.triggerDataExport),
    label: 'Trigger export',
    onSettled: onTriggerSettled,
  });

  const selected = useMemo(() => definitions.find(d => definitionKey(d) === selectedKey), [definitions, selectedKey]);
  const disabled = !selected;

  const onSubmit = () => {
    if (!selected) return;
    invoke(
      {
        organizationId,
        shapeId: selected.shapeId,
        configName: selected.name,
        rangeStart: range.firstIncluded().toString(),
        rangeEnd: range.lastIncluded().toString(),
        channelIds,
        dryRun,
      },
      dryRun
    );
  };

  return (
    <SectionShell title="Configure export">
      <div className={vertical ? classes.controlsColumn : classes.controlsRow}>
        <ExportPicker
          definitions={definitions}
          value={selectedKey}
          onChange={setSelectedKey}
          disabled={loading}
          className={fullWhenVertical}
        />
        <div className={`${classes.fieldDateRange} ${fullWhenVertical}`}>
          <DateFilter value={range} onChange={setRange} />
        </div>
        <ChannelSelectInput
          channels={channels}
          value={channelIds}
          onChange={setChannelIds}
          loading={loading}
          className={`${classes.fieldChannelMulti} ${fullWhenVertical}`}
        />
      </div>
      {/* Trigger button first (primary action), Dry-run after. The
          Dry-run checkbox is always in the DOM so the action row's
          height stays constant — only its visibility flips with
          advanced mode (Shift+D). Without the reserved slot, the
          history table below would jump down by ~6–10px each time
          the operator toggled advanced mode. */}
      <div className={classes.actionRow}>
        <InvokeButton label="Trigger export" loading={loading} disabled={disabled} onClick={onSubmit} />
        <DryRunControl
          value={dryRun}
          onChange={setDryRun}
          disabled={loading}
          className={advancedMode ? undefined : classes.hiddenReserved}
        />
      </div>
      {/* Verbose result panel (run id + raw coordinator response) is
          debug noise for the everyday operator — the toaster already
          surfaces success/failure on every trigger. Gate the panel on
          advanced mode so it shows up alongside Dry-run when Shift+D
          is held. */}
      {advancedMode && <ResultDisplay result={result} />}
    </SectionShell>
  );
};

/* ---------- pickers ---------- */

/**
 * Export picker — uses the shared `SelectInput` (react-select) component
 * so the field matches the Analytics filter selects (searchable,
 * portal-rendered menu, themed via the styled-components spicy theme).
 * `SelectInput` takes options as a `{ value: label }` map and emits the
 * selected option's value through `onChange`.
 */
export const ExportPicker: FC<{
  definitions: DataExportDefinition[];
  value: string;
  onChange: (key: string) => void;
  disabled?: boolean;
  className?: string;
}> = ({ definitions, value, onChange, disabled, className }) => {
  const classes = useSharedStyles();
  const options = useMemo(
    () =>
      definitions.reduce<Record<string, string>>((acc, d) => {
        acc[definitionKey(d)] = exportLabel(d.name);
        return acc;
      }, {}),
    [definitions]
  );
  return (
    <div className={`${classes.fieldExport} ${className ?? ''}`}>
      <SelectInput
        name="export"
        options={options}
        value={value}
        onChange={(key?: string) => onChange(key ?? '')}
        placeholder={definitions.length === 0 ? 'No exports configured' : 'Select an export…'}
        isDisabled={disabled || definitions.length === 0}
        isClearable
        sortOptions
        menuWidthFollowContent={false}
      />
    </div>
  );
};

/**
 * Channels picker — the Analytics-style `SelectInput` in multi mode
 * (`isMulti`), so it matches the Export picker while still letting the
 * operator pick several delivery channels. Channel ids are numeric;
 * `SelectInput` round-trips option values as strings, so the array is
 * mapped back to numbers on change.
 */
export const ChannelSelectInput: FC<{
  channels: DataExportDeliveryChannel[];
  value: number[];
  onChange: (ids: number[]) => void;
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}> = ({ channels, value, onChange, loading, disabled, placeholder = 'Channels (optional)', className }) => {
  const options = useMemo(
    () =>
      channels.reduce<Record<string, string>>((acc, c) => {
        acc[c.id] = c.name;
        return acc;
      }, {}),
    [channels]
  );
  return (
    <div className={className}>
      <SelectInput
        isMulti
        name="channels"
        options={options}
        value={value}
        onChange={(ids?: string[]) => onChange(Array.isArray(ids) ? ids.map(Number) : [])}
        placeholder={placeholder}
        isDisabled={loading || disabled || channels.length === 0}
        isClearable
        sortOptions
        menuWidthFollowContent={false}
      />
    </div>
  );
};

/* ---------- history table (batch-centric) ---------- */

const HISTORY_COLSPAN = 9;

export const diagnosticsExpansionOpen = (advancedMode: boolean, rowOpen: boolean) => advancedMode && rowOpen;

const HISTORY_POLL_INTERVAL_MS = 10_000;
/**
 * How long to keep polling after a mutation settles even when no
 * visible batch is nonterminal. Covers the trigger race: the deployed
 * trigger path invokes the coordinator asynchronously (Event), so the
 * batch row is inserted AFTER the mutation settles — the settle-time
 * refetch misses it, and with nothing nonterminal on screen the
 * status-driven condition alone would never arm the poll.
 */
const POST_ACTION_POLL_WINDOW_MS = 30_000;

/**
 * Poll while any visible batch can still change server-side
 * (pending/running are the only nonterminal presented statuses), or
 * during the post-action grace window. Quiet otherwise — the history
 * table is an admin surface and should not poll at rest.
 */
export const shouldPollHistory = (batches: Pick<DataExportBatch, 'status'>[], postActionGrace: boolean): boolean =>
  postActionGrace || batches.some(b => b.status === 'pending' || b.status === 'running');

interface BatchHistoryTableProps {
  organizationId: number;
  triggeredBy: DataExportBatchesVars['triggeredBy'];
  /**
   * The org's ACTIVE channels — threaded into the per-batch deliveries
   * expansion so it can grey the Redeliver button on rows whose channel
   * has since been voided.
   */
  channels: DataExportDeliveryChannel[];
  /** Gates the production window-run diagnostics expansion. */
  advancedMode: boolean;
  refreshKey: number;
  emptyMessage: string;
  title: string;
  /** Fired after any row-level mutation settles (redeliver, retry). */
  onActionSettled: () => void;
  /** Hide the "Triggered" column (redundant when every row shares a trigger source). */
  hideTriggeredColumn?: boolean;
  /** Hide the "Created" column. */
  hideCreatedColumn?: boolean;
  /** Hide the standalone refresh button rendered below the table. */
  hideRefreshButton?: boolean;
}

/**
 * Top-level history view under the merged-batch model. One row per
 * BATCH — the operator-facing AND consumer-facing unit (one merged
 * artifact per batch, deliveries attach to the batch).
 *
 * Row actions: Download (merged artifact), Redeliver batch, Retry
 * failed windows. The expansion shows the batch's delivery rows (with
 * per-delivery redeliver) plus an admin-only run diagnostics section —
 * runs are window parts with no consumer-facing actions.
 */
export const BatchHistoryTable: FC<BatchHistoryTableProps> = ({
  organizationId,
  triggeredBy,
  channels,
  advancedMode,
  refreshKey,
  emptyMessage,
  title,
  onActionSettled,
  hideTriggeredColumn = false,
  hideCreatedColumn = false,
  hideRefreshButton = false,
}) => {
  const classes = useSharedStyles();
  const [page, setPage] = useState(0);
  // The leading expand cell only carries the row-diagnostics chevron,
  // which exists in advanced mode. Outside advanced mode it would be an
  // empty checkbox-width column that visibly indents every row, so we
  // drop it entirely and let the first real column sit flush-left.
  const showExpandColumn = advancedMode;
  const colSpan =
    HISTORY_COLSPAN - (showExpandColumn ? 0 : 1) - (hideTriggeredColumn ? 1 : 0) - (hideCreatedColumn ? 1 : 0);

  const { data, loading, error, refetch } = useQuery<DataExportBatchesData, DataExportBatchesVars>(
    DATA_EXPORT_BATCHES,
    {
      variables: { organizationId, triggeredBy, page, pageSize: HISTORY_PAGE_SIZE },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    }
  );

  const [postActionGrace, setPostActionGrace] = useState(false);

  React.useEffect(() => {
    if (refreshKey === 0) return;
    refetch().catch(() => undefined);
    setPostActionGrace(true);
    const timeout = setTimeout(() => setPostActionGrace(false), POST_ACTION_POLL_WINDOW_MS);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const batches = data?.dataExportBatches?.batches ?? [];
  const totalCount = data?.dataExportBatches?.totalCount ?? 0;

  // Auto-poll while something on screen can still change (same idiom
  // as ScheduledTasksMonitor's running-executions poll).
  const polling = shouldPollHistory(batches, postActionGrace);
  React.useEffect(() => {
    if (!polling) return;
    const interval = setInterval(() => {
      refetch().catch(() => undefined);
    }, HISTORY_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [polling, refetch]);

  const triggerKey = triggeredBy.join(',');
  React.useEffect(() => {
    setPage(0);
  }, [triggerKey]);

  return (
    <SectionShell title={title}>
      {error && batches.length === 0 ? (
        <div className={classes.tableEmpty}>
          <Typography variant="body2" color="error">
            Could not load history: {error.message}
          </Typography>
        </div>
      ) : batches.length === 0 && !loading ? (
        <div className={classes.tableEmpty}>
          <Typography variant="body2">{emptyMessage}</Typography>
        </div>
      ) : (
        <>
          <TableContainer className={classes.tableContainer}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {showExpandColumn && <TableCell padding="checkbox" className={classes.headCell} />}
                  <TableCell className={classes.headCell}>Export</TableCell>
                  <TableCell className={classes.headCell}>Range</TableCell>
                  <TableCell className={classes.headCell}>Status</TableCell>
                  {!hideTriggeredColumn && <TableCell className={classes.headCell}>Triggered</TableCell>}
                  {!hideCreatedColumn && <TableCell className={classes.headCell}>Created</TableCell>}
                  <TableCell className={classes.headCell}>Completed</TableCell>
                  <TableCell align="right" className={classes.headCell}>
                    ID
                  </TableCell>
                  <TableCell align="right" className={classes.headCell}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batches.map(batch => (
                  <BatchRow
                    key={batch.id}
                    batch={batch}
                    organizationId={organizationId}
                    channels={channels}
                    advancedMode={advancedMode}
                    onActionSettled={onActionSettled}
                    showExpandColumn={showExpandColumn}
                    hideTriggeredColumn={hideTriggeredColumn}
                    hideCreatedColumn={hideCreatedColumn}
                    colSpan={colSpan}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            rowsPerPage={HISTORY_PAGE_SIZE}
            rowsPerPageOptions={[HISTORY_PAGE_SIZE]}
            page={page}
            onPageChange={(_, next) => setPage(next)}
          />
          {/* Suppressed while auto-polling — a caption blinking every
              poll tick is noise; it stays for explicit refreshes when
              the table is otherwise at rest. */}
          {loading && batches.length > 0 && !polling && (
            <Typography variant="caption" color="textSecondary">
              Refreshing…
            </Typography>
          )}
          {!hideRefreshButton && (
            <div className={classes.actionRow}>
              <Tooltip title="Refresh history">
                <IconButton size="small" onClick={() => refetch().catch(() => undefined)} aria-label="refresh-history">
                  <RefreshIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </div>
          )}
        </>
      )}
    </SectionShell>
  );
};

/* ---------- batch row ---------- */

/**
 * Local refresh signal for an embedded BatchDeliveriesExpansion —
 * redeliver actions on the owning row insert new delivery rows the
 * expansion query would otherwise miss until collapse + re-expand.
 */
const useDeliveriesRefreshKey = () => {
  const [refreshKey, setRefreshKey] = useState(0);
  const bump = () => setRefreshKey(k => k + 1);
  return { refreshKey, bump };
};

const BatchRow: FC<{
  batch: DataExportBatch;
  organizationId: number;
  channels: DataExportDeliveryChannel[];
  advancedMode: boolean;
  onActionSettled: () => void;
  showExpandColumn?: boolean;
  hideTriggeredColumn?: boolean;
  hideCreatedColumn?: boolean;
  colSpan?: number;
}> = ({
  batch,
  organizationId,
  channels,
  advancedMode,
  onActionSettled,
  showExpandColumn = false,
  hideTriggeredColumn = false,
  hideCreatedColumn = false,
  colSpan = HISTORY_COLSPAN,
}) => {
  const classes = useSharedStyles();
  const fmt = useTimestampFormatter();
  const [open, setOpen] = useState(false);
  const { refreshKey: deliveriesRefreshKey, bump: bumpDeliveries } = useDeliveriesRefreshKey();
  React.useEffect(() => {
    if (!advancedMode) setOpen(false);
  }, [advancedMode]);

  return (
    <>
      <TableRow hover>
        {showExpandColumn && (
          <TableCell padding="checkbox">
            <IconButton size="small" onClick={() => setOpen(o => !o)} aria-label="expand-batch">
              {open ? <KeyboardArrowUpIcon fontSize="inherit" /> : <KeyboardArrowDownIcon fontSize="inherit" />}
            </IconButton>
          </TableCell>
        )}
        <TableCell className={classes.cellNowrap}>{exportLabel(batch.configName)}</TableCell>
        <TableCell className={classes.cellNowrap}>{formatWindow(batch.rangeStart, batch.rangeEnd)}</TableCell>
        <TableCell>
          {/*
            Production+merge lifecycle only. Delivery outcomes are NOT
            rolled into `batch.status` — a `success` batch can still
            have failed deliveries; those surface in the expansion's
            delivery rows (and on the Scheduled tab via
            `hasFailedDeliveries`-driven tooling server-side).
          */}
          <BatchStatusChip status={batch.status} />
        </TableCell>
        {!hideTriggeredColumn && <TableCell className={classes.cellNowrap}>{batch.triggeredBy}</TableCell>}
        {!hideCreatedColumn && <TableCell className={classes.cellTimestamp}>{fmt(batch.createdAt)}</TableCell>}
        <TableCell className={classes.cellTimestamp}>{fmt(batch.completedAt)}</TableCell>
        <TableCell align="right">
          <CopyableRunId value={batch.id} label="Copy batch id" />
        </TableCell>
        <BatchActionsCell
          batch={batch}
          organizationId={organizationId}
          onActionSettled={() => {
            onActionSettled();
            bumpDeliveries();
          }}
        />
      </TableRow>
      {/* Diagnostics expansion is an advanced-mode-only surface. Skip the
          row entirely outside advanced mode so the table doesn't carry an
          empty, divider-bearing row under every batch. */}
      {advancedMode && (
        <TableRow>
          <TableCell className={classes.expansionCell} colSpan={colSpan}>
            <Collapse in={diagnosticsExpansionOpen(advancedMode, open)} timeout="auto" unmountOnExit>
              {batch.hasDeliveries && (
                <BatchDeliveriesExpansion
                  organizationId={organizationId}
                  batchId={batch.id}
                  channels={channels}
                  refreshKey={deliveriesRefreshKey}
                  onRedeliverSettled={onActionSettled}
                />
              )}
              <RunDiagnostics runs={batch.runs} />
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

/* ---------- batch row actions ---------- */

const BatchActionsCell: FC<{
  batch: DataExportBatch;
  organizationId: number;
  onActionSettled: () => void;
}> = ({ batch, organizationId, onActionSettled }) => {
  const classes = useSharedStyles();
  const { loading: redeliverLoading, invoke: invokeRedeliver } = useInvoker<RedeliverBatchData, RedeliverBatchVars>({
    mutation: REDELIVER_BATCH,
    extractResult: d => redeliverOutcome(d?.redeliverBatch),
    label: 'Redeliver batch',
    onSettled: onActionSettled,
  });
  const { loading: retryLoading, invoke: invokeRetry } = useInvoker<RetryDataExportBatchData, RetryDataExportBatchVars>(
    {
      mutation: RETRY_DATA_EXPORT_BATCH,
      extractResult: d => backfillOutcome(d?.retryDataExportBatch),
      label: 'Retry failed windows',
      onSettled: onActionSettled,
    }
  );

  // Mirrors the server-side gates: download + redeliver need the MERGED
  // artifact (`artifactLocation` non-null), redeliver additionally needs
  // at least one existing delivery row (stage-only batches have nothing
  // to re-push), and retry only applies to terminal production failures.
  const hasArtifact = batch.artifactLocation != null;
  const canRedeliver = hasArtifact && batch.hasDeliveries;
  const canRetry = batch.status === 'failed';

  return (
    <TableCell align="right" className={classes.cellActions}>
      <BatchDownloadButton
        organizationId={organizationId}
        batchId={batch.id}
        enabled={hasArtifact}
        tooltip={hasArtifact ? 'Download the merged artifact' : 'No merged artifact yet'}
      />
      <Tooltip
        title={
          canRedeliver
            ? 'Redeliver the merged artifact to every channel that originally received it'
            : !batch.hasDeliveries
              ? 'No deliveries to redeliver (stage-only batch)'
              : 'No merged artifact to redeliver yet'
        }
      >
        <span>
          <IconButton
            size="small"
            onClick={() => invokeRedeliver({ organizationId, batchId: batch.id }, false)}
            disabled={!canRedeliver || redeliverLoading}
            aria-label="redeliver-batch"
          >
            {redeliverLoading ? <CircularProgress size={14} /> : <ReplayIcon fontSize="inherit" />}
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip
        title={
          canRetry
            ? 'Retry failed windows — re-runs production for this batch (no new delivery rows are created)'
            : 'Nothing to retry — only failed batches can be retried'
        }
      >
        <span>
          <IconButton
            size="small"
            onClick={() => invokeRetry({ organizationId, batchId: batch.id }, false)}
            disabled={!canRetry || retryLoading}
            aria-label="retry-batch"
          >
            {retryLoading ? <CircularProgress size={14} /> : <AutorenewIcon fontSize="inherit" />}
          </IconButton>
        </span>
      </Tooltip>
      {batch.errorMessage && (
        <Tooltip title={batch.errorMessage} arrow>
          {/* `batch.errorMessage` carries the merge-phase failure cause;
              per-window production failures live on the run rows in the
              diagnostics expansion. */}
          <Typography component="span" variant="caption" color="error" className={classes.deliveryErrorLabel}>
            merge error
          </Typography>
        </Tooltip>
      )}
    </TableCell>
  );
};

/* ---------- run diagnostics expansion (admin-only, no actions) ---------- */

/**
 * Window-part runs of the batch, oldest-first (chronological window
 * order). Pure production diagnostics under the merged-batch model:
 * runs carry no delivery state, no artifact actions, no redeliver —
 * the merged batch artifact is the only consumer-facing object. The
 * run id is still surfaced because operators paste it into runbook
 * SQL when a window breaks.
 */
const RunDiagnostics: FC<{ runs: DataExportRun[] }> = ({ runs }) => {
  const classes = useSharedStyles();
  const fmt = useTimestampFormatter();
  if (runs.length === 0) return null;
  return (
    <Box className={classes.deliverySubTable} padding={1}>
      <Typography variant="caption" color="textSecondary">
        Window runs (production diagnostics)
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Window</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Rows</TableCell>
            <TableCell align="right">Bytes</TableCell>
            <TableCell>Completed</TableCell>
            <TableCell>Error</TableCell>
            <TableCell>Run ID</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {runs.map(run => (
            <TableRow key={run.id}>
              <TableCell className={classes.cellNowrap}>{formatWindow(run.windowStart, run.windowEnd)}</TableCell>
              <TableCell>
                <RunStatusChip status={run.status} />
              </TableCell>
              <TableCell align="right">{run.rowCount == null ? '—' : run.rowCount.toLocaleString()}</TableCell>
              <TableCell align="right">{run.byteCount == null ? '—' : run.byteCount.toLocaleString()}</TableCell>
              <TableCell className={classes.cellTimestamp}>{fmt(run.completedAt)}</TableCell>
              <TableCell>
                {run.errorMessage ? (
                  <Tooltip title={run.errorMessage}>
                    <Typography variant="caption" color="error">
                      {run.errorPhase ?? 'error'}
                    </Typography>
                  </Tooltip>
                ) : (
                  '—'
                )}
              </TableCell>
              <TableCell>
                <CopyableRunId value={run.id} label="Copy run id" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};
